import React, { useState, useEffect } from "react";
import { BaseModal } from "../ui/BaseModal.tsx";
import { CustomToggleTabs } from "../ui/CustomToggleTabs.tsx";
import { CustomAlert } from "../ui/CustomAlert.tsx";
import { Boxes, Info, Edit, X, Lock, Plus } from "lucide-react";
import Cookies from "js-cookie";
import { User } from "../ui/types";
import { getUsers,} from "../../services/associationApi";
import { getGroups, getGroupUsers, deleteGroup, updateGroup, createGroup } from "../../services/groupApi.jsx";
import { useTheme } from "../ThemeContext";
import CollapsibleItemsList from "../ui/CollapsibleItemsList.tsx";

interface GroupModalProps {
  onClose: () => void;
  isOpen: boolean;
}

interface Group {
  groupname: string;
  description: string;
  users?: { username: string }[];
  creation_date?: string;
  last_modified_date?: string;
}

interface GroupFormData {
  name: string;
  description: string;
  selectedUsers: string[];
}

const InfoBox = ({ children, isDark }) => {
  return (
    <div
      className={`flex items-start p-2 rounded-lg mb-4 border border-green-500 ${
        isDark ? "bg-green-900/10" : "bg-green-50"
      }`}
    >
      {children}
    </div>
  );
};

const ReadOnlyField = ({ children, isDark }) => {
  return (
    <div
      className={`relative p-3 flex items-center justify-between w-full rounded-lg text-sm ${
        isDark
          ? "bg-[#3F3F46] text-[#D4D4D8] border border-[#52525B]"
          : "bg-[#E5E7EB] text-[#6B7280] border border-[#D1D5DB]"
      }`}
    >
      {children}
    </div>
  );
};

const GroupModal = ({ onClose, isOpen }: GroupModalProps) => {
  const { isDarkMode } = useTheme();
  const [selectedTab, setSelectedTab] = useState("view");
  const [groups, setGroups] = useState<Group[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [formData, setFormData] = useState<GroupFormData>({
    name: "",
    description: "",
    selectedUsers: [],
  });
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);
  const [groupsExpanded, setGroupsExpanded] = useState(true);
  const [nameError, setNameError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCount, setSelectedCount] = useState(0);
  const [textConfig, setTextConfig] = useState<any>({});
  const [alertConfig, setAlertConfig] = useState({
    open: false,
    type: "success" as "success" | "error" | "loading" | "confirm",
    message: "",
    title: "",
    onConfirm: undefined as (() => Promise<void>) | undefined,
  });

  const fetchTextConfig = async () => {
    try {
      const response = await fetch("/text.json");
      const data = await response.json();
      setTextConfig(data);
    } catch (error) {
      console.error("Error loading text config:", error);
    }
  };

  useEffect(() => {
    fetchTextConfig();
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchGroups();
      fetchUsers();
    }
  }, [isOpen]);

  // useEffect comentado para futura reutilización
  // useEffect(() => {
  //   validateGroupName(formData.name);
  // }, [formData.name]);

  useEffect(() => {
    setSelectedCount(formData.selectedUsers.length);
  }, [formData.selectedUsers]);

  // Función de validación comentada para futura reutilización
  // const validateGroupName = (name: string) => {
  //   if (!name) {
  //     setNameError(null);
  //     return;
  //   }

  //   if (name.includes(" ")) {
  //     setNameError(textConfig.GROUP_MODAL_NAME_NO_SPACES_ERROR);
  //     return;
  //   }

  //   if (
  //     !editingGroup &&
  //     groups.some(
  //       (group) => group.groupname.toLowerCase() === name.toLowerCase()
  //     )
  //   ) {
  //     setNameError(textConfig.GROUP_MODAL_NAME_EXISTS_ERROR);
  //     return;
  //   }

  //   setNameError(null);
  // };

  const fetchGroups = async () => {
    try {
      setIsLoading(true);
      const data = await getGroups();
      setGroups(data);
    } catch (error) {
      console.error(textConfig.GROUP_MODAL_ERROR_LOADING_GROUPS || "Error al cargar grupos:", error);
      setAlertConfig({
        open: true,
        type: "error",
        message: textConfig.GROUP_MODAL_ERROR_LOADING_GROUPS_MESSAGE,
        title: textConfig.GROUP_MODAL_ERROR_TITLE,
        onConfirm: undefined,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const usersData = await getUsers();
      setUsers(Array.isArray(usersData) ? usersData : []);
    } catch (error) {
      console.error(textConfig.GROUP_MODAL_ERROR_LOADING_USERS || "Error al cargar usuarios:", error);
      setAlertConfig({
        open: true,
        type: "error",
        message: textConfig.GROUP_MODAL_ERROR_LOADING_USERS_MESSAGE,
        title: textConfig.GROUP_MODAL_ERROR_TITLE,
        onConfirm: undefined,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteGroup = async (groupname: string) => {
    setAlertConfig({
      open: true,
      type: "confirm",
      message: textConfig.GROUP_MODAL_DELETE_CONFIRM?.replace('{groupname}', groupname),
      title: textConfig.GROUP_MODAL_DELETE_CONFIRM_TITLE,
      onConfirm: async () => {
        try {
          setAlertConfig({
            open: true,
            type: "loading",
            message: textConfig.GROUP_MODAL_DELETING,
            title: "",
            onConfirm: undefined,
          });

          await deleteGroup(groupname);
          await fetchGroups();

          setAlertConfig({
            open: true,
            type: "success",
            message: textConfig.GROUP_MODAL_DELETE_SUCCESS,
            title: textConfig.GROUP_MODAL_SUCCESS_TITLE,
            onConfirm: undefined,
          });
        } catch (error) {
          setAlertConfig({
            open: true,
            type: "error",
            message: error.message || textConfig.GROUP_MODAL_DELETE_ERROR,
            title: textConfig.GROUP_MODAL_ERROR_TITLE,
            onConfirm: undefined,
          });
        }
      },
    });
  };

  const handleEditGroup = async (group: Group) => {
    setEditingGroup(group);
    setFormData({
      name: group.groupname,
      description: group.description || "",
      selectedUsers: [], 
    });
    setSelectedTab("create");
    
         try {
       const groupUsers = await getGroupUsers(group.groupname);
       const usernames = groupUsers.map(user => user.username || user);
       
       setFormData(prev => ({
         ...prev,
         selectedUsers: usernames
       }));
     } catch (error) {
       setFormData(prev => ({
         ...prev,
         selectedUsers: []
       }));
     }
  };

  // Función de creación de grupos comentada para futura reutilización
  // const handleCreateGroup = async () => {
  //   if (!formData.name || nameError) return;

  //   setAlertConfig({
  //     open: true,
  //     type: "loading",
  //     message: editingGroup ? textConfig.GROUP_MODAL_UPDATING : textConfig.GROUP_MODAL_CREATING,
  //     title: "",
  //     onConfirm: undefined,
  //   });

  //   try {
  //     if (editingGroup) {
  //       const updateData: any = {};
  //       updateData.description = formData.description;
  //       
  //       if (formData.selectedUsers.length > 0) {
  //         const currentUsers = await getGroupUsers(editingGroup.groupname);
  //         const currentUsernames = currentUsers.map(user => user.username || user);
  //         
  //         const usersToUpdate = {};
  //         
  //         formData.selectedUsers.forEach(username => {
  //           usersToUpdate[username] = true;
  //         });
  //         
  //         currentUsernames.forEach(username => {
  //           if (!formData.selectedUsers.includes(username)) {
  //             usersToUpdate[username] = false;
  //           }
  //         });
  //         
  //         updateData.users = usersToUpdate;
  //       }
  //       
  //       await updateGroup(editingGroup.groupname, updateData);
  //     } else {
  //       await createGroup({
  //         groupname: formData.name,
  //         description: formData.description,
  //       });
  //     }

  //     await fetchGroups();
  //     resetForm();

  //     setAlertConfig({
  //       open: true,
  //       type: "success",
  //       message: editingGroup
  //         ? textConfig.GROUP_MODAL_UPDATE_SUCCESS
  //         : textConfig.GROUP_MODAL_CREATE_SUCCESS,
  //       title: textConfig.GROUP_MODAL_SUCCESS_TITLE,
  //       onConfirm: undefined,
  //     });

  //     setSelectedTab("view");
  //   } catch (error: any) {
  //     console.error(textConfig.GROUP_MODAL_ERROR_CREATE_UPDATE || "Error al crear/actualizar grupo:", error);

  //     const errorMessage = error?.message || "";
  //     if (
  //       errorMessage.includes("ya existe") ||
  //       errorMessage.includes("already exists") ||
  //       errorMessage.includes("duplicate")
  //     ) {
  //       setAlertConfig({
  //         open: true,
  //         type: "error",
  //         message: textConfig.GROUP_MODAL_DUPLICATE_NAME_ERROR?.replace('{name}', formData.name) || errorMessage,
  //         title: textConfig.GROUP_MODAL_DUPLICATE_NAME_TITLE,
  //         onConfirm: undefined,
  //       });
  //     } else {
  //       setAlertConfig({
  //         open: true,
  //         type: "error",
  //         message: errorMessage || textConfig.GROUP_MODAL_CREATE_ERROR,
  //         title: textConfig.GROUP_MODAL_ERROR_TITLE,
  //         onConfirm: undefined,
  //       });
  //     }
  //   }
  // };

  const handleUpdateGroup = async () => {
    if (!editingGroup) return;

    setAlertConfig({
      open: true,
      type: "loading",
      message: textConfig.GROUP_MODAL_UPDATING || "Actualizando grupo...",
      title: "",
      onConfirm: undefined,
    });

         try {
       const currentUsers = await getGroupUsers(editingGroup.groupname);
       const currentUsernames = currentUsers.map(user => user.username || user);
       
       const updateData: any = {};
       
       if (formData.description !== editingGroup.description) {
         updateData.description = formData.description;
       }
       
       const usersToUpdate: { [key: string]: boolean } = {};
       let hasUserChanges = false;
       
       formData.selectedUsers.forEach(username => {
         if (!currentUsernames.includes(username)) {
           usersToUpdate[username] = true;
           hasUserChanges = true;
         }
       });
       
       currentUsernames.forEach(username => {
         if (!formData.selectedUsers.includes(username)) {
           usersToUpdate[username] = false;
           hasUserChanges = true;
         }
       });
       
       if (hasUserChanges && Object.keys(usersToUpdate).length > 0) {
         updateData.users = usersToUpdate;
       }
       
       if (Object.keys(updateData).length > 0) {
         await updateGroup(editingGroup.groupname, updateData);
       }

      await fetchGroups();
      resetForm();

      setAlertConfig({
        open: true,
        type: "success",
        message: textConfig.GROUP_MODAL_UPDATE_SUCCESS || "Grupo actualizado exitosamente",
        title: textConfig.GROUP_MODAL_SUCCESS_TITLE || "Éxito",
        onConfirm: undefined,
      });

      setSelectedTab("view");
    } catch (error: any) {
      console.error(textConfig.GROUP_MODAL_ERROR_CREATE_UPDATE || "Error al actualizar grupo:", error);

      const errorMessage = error?.message || "";
      setAlertConfig({
        open: true,
        type: "error",
        message: errorMessage || textConfig.GROUP_MODAL_CREATE_ERROR || "Error al actualizar grupo",
        title: textConfig.GROUP_MODAL_ERROR_TITLE || "Error",
        onConfirm: undefined,
      });
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      selectedUsers: [],
    });
    setEditingGroup(null);
    setSearchTerm("");
  };

  const handleCancel = () => {
    resetForm();
    setSelectedTab("view");
  };

  const filteredUsers = users.filter((user) =>
    user.username.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderViewGroups = () => (
    <div className="mt-4">
      <InfoBox isDark={isDarkMode}>
        <Info
          size={isMobile ? 22 : 38}
          className={`mr-2 ${isDarkMode ? "text-green-400" : "text-green-600"}`}
        />
        <p
          className={`text-sm ${
            isDarkMode ? "text-green-100" : "text-green-800"
          }`}
        >
          {textConfig.GROUP_MODAL_VIEW_INFO}
        </p>
      </InfoBox>

      <CollapsibleItemsList
        items={groups}
        isLoading={isLoading}
        isDarkMode={isDarkMode}
        itemsExpanded={groupsExpanded}
        setItemsExpanded={setGroupsExpanded}
        onDelete={handleDeleteGroup}
        onEdit={handleEditGroup}
        type="group"
        title={textConfig.GROUP_MODAL_EXISTING_GROUPS_TITLE}
        emptyMessage={textConfig.GROUP_MODAL_NO_GROUPS_MESSAGE}
        loadingMessage={textConfig.GROUP_MODAL_LOADING_GROUPS}
        showDeleteButton={false}
      />
    </div>
  );

  const renderEditGroup = () => (
    <div className="mt-4">
      <InfoBox isDark={isDarkMode}>
        <Info
          size={isMobile ? 22 : 38}
          className={`mr-2 ${isDarkMode ? "text-green-400" : "text-green-600"}`}
        />
        <p
          className={`text-sm ${
            isDarkMode ? "text-green-100" : "text-green-800"
          }`}
        >
          {textConfig.GROUP_MODAL_EDIT_INFO || "Editar grupo y gestionar usuarios"}
        </p>
      </InfoBox>

             {editingGroup && (
         <div className="bg-[#30503C] text-[#2ECC71] px-4 py-2 rounded-md flex items-center mb-4">
           <Edit size={18} className="mr-2" />
           <span>{textConfig.GROUP_MODAL_EDITING_GROUP?.replace('{groupname}', editingGroup.groupname) || `Editando grupo: ${editingGroup.groupname}`}</span>
         </div>
       )}
       <div className="mb-6">
         <div className="mb-4">
           <label className="block mb-2 text-sm font-medium">
             {textConfig.GROUP_MODAL_GROUP_NAME_LABEL || "Nombre del Grupo"}
           </label>
           
           <ReadOnlyField isDark={isDarkMode}>
             <span>{formData.name}</span>
             <Lock className="text-gray-500" size={16} />
           </ReadOnlyField>
         </div>

                 <div className="mb-4">
           <label className="block mb-2 text-sm font-medium">
             {textConfig.GROUP_MODAL_DESCRIPTION_LABEL || "Descripción"}
             <span className="text-xs text-gray-500 float-right">
               {formData.description.length}/50
             </span>
           </label>
           <textarea
             value={formData.description}
             onChange={(e) =>
               setFormData({ ...formData, description: e.target.value })
             }
             placeholder={textConfig.GROUP_MODAL_DESCRIPTION_PLACEHOLDER || "Descripción del grupo..."}
             maxLength={50}
             rows={2}
             className={`w-full p-2.5 ${
               isDarkMode
                 ? "bg-[#222222] border-zinc-700"
                 : "bg-gray-100 border-gray-300"
             } border rounded-lg ${
               isDarkMode ? "text-white" : "text-gray-900"
             } focus:ring-green-500 focus:border-green-500`}
           />
         </div>

        <div className="mb-4">
          <label className="block mb-2 text-sm font-medium">
            {textConfig.GROUP_MODAL_USERS_LABEL || "Gestionar Usuarios"}
          </label>
          
          <div className="mb-3">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={textConfig.GROUP_MODAL_SEARCH_USERS_PLACEHOLDER || "Buscar usuarios..."}
              className={`w-full p-2.5 ${
                isDarkMode
                  ? "bg-[#222222] border-zinc-700"
                  : "bg-gray-100 border-gray-300"
              } border rounded-lg ${
                isDarkMode ? "text-white" : "text-gray-900"
              } focus:ring-green-500 focus:border-green-500`}
            />
          </div>

          <div className={`max-h-48 overflow-y-auto border rounded-lg p-3 ${
            isDarkMode ? "border-zinc-700 bg-[#1a1a1a]" : "border-gray-300 bg-gray-50"
          }`}>
            {filteredUsers.length === 0 ? (
              <p className={`text-center py-4 text-sm ${
                isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}>
                {textConfig.GROUP_MODAL_NO_USERS_FOUND || "No se encontraron usuarios"}
              </p>
            ) : (
              filteredUsers
                .sort((a, b) => {
                  const aInGroup = formData.selectedUsers.includes(a.username);
                  const bInGroup = formData.selectedUsers.includes(b.username);
                  if (aInGroup && !bInGroup) return -1;
                  if (!aInGroup && bInGroup) return 1;
                  return a.username.localeCompare(b.username);
                })
                .map((user) => {
                  const isUserInGroup = formData.selectedUsers.includes(user.username);
                  return (
                    <div
                      key={user.username}
                      className={`flex items-center justify-between p-2 rounded hover:${
                        isDarkMode ? "bg-[#2a2a2a]" : "bg-gray-100"
                      } transition-colors ${
                        isUserInGroup ? (isDarkMode ? "bg-[#2a2a2a]/50" : "bg-green-50/50") : ""
                      }`}
                    >
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id={`user-${user.username}`}
                          checked={isUserInGroup}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            setFormData(prev => ({
                              ...prev,
                              selectedUsers: isChecked
                                ? [...prev.selectedUsers, user.username]
                                : prev.selectedUsers.filter(u => u !== user.username)
                            }));
                          }}
                          className="w-4 h-4 mr-3 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                        />
                        <label
                          htmlFor={`user-${user.username}`}
                          className={`text-sm cursor-pointer ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {user.username}
                        </label>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {(user.email || user.attributes?.email) && (
                          <span className={`text-xs ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}>
                            {user.email || user.attributes?.email}
                          </span>
                        )}
                        
                        {isUserInGroup && (
                          <button
                            onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                selectedUsers: prev.selectedUsers.filter(u => u !== user.username)
                              }));
                            }}
                            className={`p-1 rounded hover:${
                              isDarkMode ? "bg-red-900/20" : "bg-red-100"
                            } transition-colors`}
                            title="Quitar usuario del grupo"
                          >
                            <svg
                              className={`w-4 h-4 ${
                                isDarkMode ? "text-red-400" : "text-red-600"
                              }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
            )}
          </div>

          <div className="mt-2 text-sm">
            <div className="flex justify-between items-center">
              <span className={isDarkMode ? "text-gray-400" : "text-gray-600"}>
                {textConfig.GROUP_MODAL_SELECTED_USERS_COUNT?.replace('{count}', selectedCount.toString()) || 
                 `Usuarios seleccionados: ${selectedCount}`}
              </span>
              {editingGroup && (
                <span className={`text-xs px-2 py-1 rounded ${
                  isDarkMode ? "bg-blue-900/30 text-blue-300" : "bg-blue-100 text-blue-700"
                }`}>
                  Total en grupo: {selectedCount}
                </span>
              )}
              
            </div>
            
          </div>
          
                 </div>
                 <div className="flex justify-end gap-2 mb-6">
         <button
           onClick={handleCancel}
           className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded-md flex items-center gap-1"
         >
           <X size={18} />
           {textConfig.GROUP_MODAL_CANCEL_BUTTON || "Cancelar"}
         </button>
         <button
           onClick={handleUpdateGroup}
           className="px-4 py-2 rounded-md flex items-center gap-1 bg-[#2ECC71] hover:bg-[#28B463] text-white"
         >
           <Edit size={18} />
           {textConfig.GROUP_MODAL_SAVE_CHANGES_BUTTON || "Guardar Cambios"}
         </button>
       </div>

       </div>
     </div>
   );

  const getTextColorClass = () =>
    isDarkMode ? "text-gray-300" : "text-gray-600";

  return (
    <>
      <BaseModal title={textConfig.GROUP_MODAL_TITLE} onClose={onClose} isOpen={isOpen}>
        <div className="w-full max-w-2xl">
                     <p className={`mb-4 ${getTextColorClass()}`}>
             {textConfig.GROUP_MODAL_DESCRIPTION}
           </p>

           {/* Tabs comentados para futura reutilización */}
           {/* <div className="mb-4">
             <CustomToggleTabs
               value={selectedTab}
               onChange={setSelectedTab}
               options={[
                 {
                   value: "view",
                   label: textConfig.GROUP_MODAL_TAB_VIEW,
                   icon: <Boxes size={18} />,
                 },
                 {
                   value: "create",
                   label: editingGroup ? textConfig.GROUP_MODAL_TAB_EDIT : textConfig.GROUP_MODAL_TAB_CREATE,
                   icon: editingGroup ? <Edit size={18} /> : <Plus size={18} />,
                 },
               ]}
             />
           </div> */}

           {selectedTab === "view" ? renderViewGroups() : renderEditGroup()}
        </div>
      </BaseModal>

      <CustomAlert
        open={alertConfig.open}
        type={alertConfig.type}
        message={alertConfig.message}
        onClose={() => setAlertConfig((prev) => ({ ...prev, open: false }))}
        textConfig={textConfig}
        title={alertConfig.title}
        onConfirm={alertConfig.onConfirm}
        $isDark={isDarkMode}
      />
    </>
  );
};

export default GroupModal;